const lazyVideos = Array.from(document.querySelectorAll("video.lazy-video"));

// Track selected video and loop handlers for each video
/** @type {HTMLVideoElement | null} */
let selectedVideo = null;
/** @type {WeakMap<HTMLVideoElement, () => void>} */
const videoLoopHandlers = new WeakMap();

// Function to remove loop from a video
/**
 * @param {HTMLVideoElement} video
 */
function removeLoopFromVideo(video) {
  if (video) {
    const handler = videoLoopHandlers.get(video);
    if (handler) {
      video.removeEventListener('timeupdate', handler);
      videoLoopHandlers.delete(video);
    }
  }
}

// Function to apply loop to selected video only
/**
 * @param {HTMLVideoElement} video
 */
function applyLoopToVideo(video) {
  // Remove loop from previously selected video
  if (selectedVideo && selectedVideo !== video) {
    removeLoopFromVideo(selectedVideo);
  }
  
  // Set new selected video
  selectedVideo = video;
  
  // Get loop duration from data attribute
  const loopDuration = parseInt(video.dataset.loopDuration || '0');
  
  // Only apply loop if loopDuration is valid and not -1
  if (loopDuration && loopDuration !== -1 && loopDuration > 0) {
    // Remove any existing handler for this video
    removeLoopFromVideo(video);
    
    // Create new loop handler
    const loopHandler = function() {
      if (video.currentTime >= loopDuration) {
        video.currentTime = 0;
      }
    };
    
    // Store handler and add event listener
    videoLoopHandlers.set(video, loopHandler);
    video.addEventListener('timeupdate', loopHandler);
  }
}

// Function to add selection handlers to a video
/**
 * @param {HTMLVideoElement} video
 */
function addSelectionHandlers(video) {
  // Only add handlers if video has custom-video-click class and hasn't been set up yet
  if (video.classList.contains('custom-video-click') && !video.dataset.selectionHandlersAdded) {
    // Mark as having handlers added
    video.dataset.selectionHandlersAdded = 'true';
    
    // On hover, select this video
    video.addEventListener('mouseenter', function() {
      const loopDuration = parseInt(video.dataset.loopDuration || '0');
      if (loopDuration && loopDuration !== -1) {
        applyLoopToVideo(video);
      }
    });
    
    // On click, select this video
    video.addEventListener('click', function() {
      const loopDuration = parseInt(video.dataset.loopDuration || '0');
      if (loopDuration && loopDuration !== -1) {
        applyLoopToVideo(video);
      }
    });
  }
}

if ("IntersectionObserver" in window) {
  var lazyVideoObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      /** @type {HTMLVideoElement} */
      const video = /** @type {HTMLVideoElement} */ (entry.target);

      if (entry.isIntersecting) {
        if (video) {
          // Lazy load the poster
          if (video.dataset.poster) {
            video.poster = video.dataset.poster;
          }

          // Set the source URLs for each <source> element
          const sources = video.querySelectorAll("source");
          sources.forEach((source) => {
            if (source.dataset.src) {
              source.src = source.dataset.src;
            }
          });

          // Load and autoplay the video
          video.load();
          
          // Get the container to remove shimmer effect
          const container = video.closest('.ugc-video-container');
          
          // Show video when it's ready to play
          const markAsLoaded = function() {
            video.classList.add('loaded');
            if (container) {
              container.classList.add('video-loaded');
            }
          };
          
          video.addEventListener('loadeddata', markAsLoaded, { once: true });
          
          // Fallback: show video even if loadeddata doesn't fire
          video.addEventListener('canplay', markAsLoaded, { once: true });
          
          // Ensure poster is visible immediately
          if (video.poster) {
            markAsLoaded();
          }
          
          video.play();
          
          // Add selection handlers for custom-video-click videos
          addSelectionHandlers(video);
          
          // Only apply loop if this video is selected and has loop_duration
          // For custom-video-click videos, loop will be applied on selection
          const loopDuration = parseInt(video.dataset.loopDuration || '0');
          if (loopDuration && loopDuration !== -1 && loopDuration > 0) {
            // Check if video has custom-video-click class (should only loop when selected)
            if (video.classList.contains('custom-video-click')) {
              // Don't apply loop automatically - wait for selection
              // But if it's the first intersecting video, select it
              if (!selectedVideo) {
                applyLoopToVideo(video);
              }
            } else {
              // For other videos, apply loop normally if they have loop_duration
              applyLoopToVideo(video);
            }
          }

          // Remove lazy class and unobserve the video
          video.classList.remove("lazy-video");
          lazyVideoObserver.unobserve(video);
        }
      } else {
        // Check if video exists and is currently playing before pausing
        if (video && !video.paused) {
          video.pause();
        }
        // If this was the selected video and it's no longer intersecting, remove loop
        if (video === selectedVideo && video.classList.contains('custom-video-click')) {
          removeLoopFromVideo(video);
          selectedVideo = null;
        }
      }
    });
  });

  lazyVideos.forEach((lazyVideo) => {
    lazyVideoObserver.observe(lazyVideo);
  });
}